import React, { useCallback, useEffect, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import type { Medication } from '@components/Patient/MedicationsDataBlock';
import { useSubjectLanguageVariants } from '@features/user';
import { useAutoSave } from '@hooks/useAutoSave';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useUpdatePatientModuleMutation } from '../store/api';
import type { PatientModuleInfo } from '../types';

import { PatientMedicationsEditCard } from './PatientMedicationsEditCard';

interface PatientMedicationsEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  medications: Medication[];
  moduleInfo: PatientModuleInfo;
  onMedicationsUpdated?: () => void;
}

export const PatientMedicationsEditModal: React.FC<PatientMedicationsEditModalProps> = ({
  open,
  onClose,
  patientId,
  medications: initialMedications,
  moduleInfo,
  onMedicationsUpdated,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const [medications, setMedications] = useState<Medication[]>(initialMedications);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  const [updateModule, { isLoading }] = useUpdatePatientModuleMutation();

  // Reset medications when modal opens/closes or initial medications change
  useEffect(() => {
    if (open) {
      setMedications(initialMedications);
      setHasLocalChanges(false);
    }
  }, [open, initialMedications]);

  const performSave = useCallback(
    async (medsToSave: Medication[]) => {
      try {
        // Transform medications back to the format expected by the API
        const transformedData = medsToSave.map(med => ({
          name: med.medication_name,
          medication_name: med.medication_name,
          generic_name: med.generic_name,
          formulation: med.formulation,
          strength: med.strength,
          route_of_administration: med.route_of_administration,
          dosage_amount: med.dosage_amount,
          dosage: med.dosage_amount || med.strength,
          frequency: med.frequency,
          timing: med.timing,
          start_date: med.start_date,
          end_date: med.end_date,
          indication: med.indication,
          notes: med.indication,
          active: med.isActive ?? true,
        }));

        await updateModule({
          patientId,
          patientModuleId: moduleInfo.patient_module_id,
          dataKey: moduleInfo.dataKey,
          data: transformedData,
        }).unwrap();
        setHasLocalChanges(false);
        onMedicationsUpdated?.();
      } catch (error) {
        console.error('Failed to save medications:', error);
        throw error;
      }
    },
    [patientId, moduleInfo, updateModule, onMedicationsUpdated],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt } = useAutoSave({
    onSave: performSave,
    debounceMs: 3000,
    enabled: open && hasLocalChanges,
    flushOnUnmount: true, // Safety net for unexpected unmounts
  });

  const handleMedicationsChange = useCallback(
    (newMedications: Medication[]) => {
      setMedications(newMedications);
      setHasLocalChanges(true);
      triggerAutoSave(newMedications);
    },
    [triggerAutoSave],
  );

  // Handle modal close with flush of pending saves
  const handleClose = useCallback(async () => {
    // If there are unsaved changes, flush immediately before closing
    if (hasLocalChanges && medications.length >= 0) {
      await saveImmediately(medications);
    }
    onClose();
  }, [hasLocalChanges, medications, saveImmediately, onClose]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing during active save operation
        if (status === 'saving') {
          return;
        }
        handleClose();
      }}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? '100%' : '80vh',
          maxHeight: fullScreen ? '100%' : '80vh',
          bgcolor: 'background.paper',
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {`Edit ${singularCap} Medications`}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={status === 'saving'}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', pt: 2, px: 2 }}>
          <PatientMedicationsEditCard
            medications={medications}
            onMedicationsChange={handleMedicationsChange}
            isLoading={isLoading}
            patientId={patientId}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <AutosaveStatus
          status={status}
          isDirty={hasLocalChanges}
          lastSavedAt={lastSavedAt}
          verbose
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          disabled={status === 'saving'}
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          {status === 'saving' ? 'Saving...' : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
